import React, {
  useEffect, useRef, useMemo, useCallback, useState,
} from 'react';

import { ChevronDoubleRightIcon, FilterIcon } from '@vlabs/icons';
import { FiltersHeader } from '@vlabs/shared/components/filters-panel/FiltersHeader';
import { useDebouncedSetFilters } from '@vlabs/shared/components/filters-panel/hooks';
import { MapCell } from '@vlabs/shared/components/map-cell/MapCell';
import {
  Page, Table, Panel, Control, PageWithSidebar, Badge,
} from '@vlabs/uikit';
import cn from 'classnames';
import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';
import { throttle } from 'lodash';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { selectAnalyticData } from '@vlabs/pages/video-analytics/selectors';

import VideoAnalyticFilterForm from './components/VideoAnalyticFilterForm';
import { VideoAnalyticImageCard } from './components/VideoAnalyticImageCard';
import { VideoAnalyticInfoCell } from './components/VideoAnalyticInfoCell';
import './VideoAnalyticListPage.sass';
import { defaultFilters } from './defaultFilters';
import { useGeneralEventsTableData } from './useGeneralEventsTableData';

const STORAGE_KEY = 'filtersVideoAnalyticListPage';

function ImageCell({ row }) {
  return <VideoAnalyticImageCard {...row.original} />;
}
function InfoCell({ value }) {
  return <VideoAnalyticInfoCell {...value} />;
}
function LocationCell({ value }) {
  return (
    <MapCell
      className="VideoAnalyticArchive__MapCell"
      original={value}
    />
  );
}

function VideoAnalyticListPageComponent({ lpStreamOptions }) {
  const { t } = useTranslation();
  const lastScrollTop = useRef(0);
  const tableRef = useRef(null);

  const [isSidebarOpened, setIsSidebarOpened] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);

  const [filters, setFilters] = useState({});
  const [filterCounts, setFilterCounts] = useState(0);
  const [isReady, setIsReady] = useState(false);

  const form = useForm({ defaultValues: defaultFilters });
  const { watch, trigger, reset } = form;

  useEffect(() => {
    (async () => {
      const savedFilters = await idbGet(STORAGE_KEY);

      const hasSaved = savedFilters && typeof savedFilters === 'object';
      const formValues = hasSaved ? { ...defaultFilters, ...savedFilters } : defaultFilters;

      setFilters(formValues);
      reset(formValues);
      setIsReady(true);
    })();
  }, [reset]);

  const setFiltersStorage = useCallback((next) => {
    if (next) {
      idbSet(STORAGE_KEY, next);
      setFilters(next);
    } else {
      idbDel(STORAGE_KEY);
      setFilters({});
    }
  }, []);

  const tableProps = useGeneralEventsTableData({ isReady, filters });

  const resetFilters = useCallback(() => {
    reset(defaultFilters);
    setFilters({});
    idbDel(STORAGE_KEY);
  }, [reset]);

  useDebouncedSetFilters({ trigger, watch }, setFiltersStorage);

  useEffect(() => {
    const tableBody = document.querySelector('.TableBody');
    if (!tableBody) return;

    const handleScroll = throttle(() => {
      const { scrollTop, scrollHeight, clientHeight } = tableBody;
      if (scrollHeight === clientHeight) {
        setIsHeaderVisible(true);
      }

      // если мы в самом низу таблицы — просто выходим
      if (scrollTop + clientHeight >= scrollHeight - 1) return;

      if (Math.abs(scrollTop - lastScrollTop.current) < 10) return;

      setIsHeaderVisible(scrollTop <= lastScrollTop.current);
      lastScrollTop.current = scrollTop;
    }, 100);

    tableBody.addEventListener('scroll', handleScroll);
    // eslint-disable-next-line consistent-return
    return () => tableBody.removeEventListener('scroll', handleScroll);
  }, []);

  const actionButtons = (
    <Control.Button
      data-testid="filtersSidebar.toggleButton"
      icon={isSidebarOpened ? <ChevronDoubleRightIcon /> : <FilterIcon />}
      kind={isSidebarOpened ? 'secondary' : 'primary'}
      onClick={() => setIsSidebarOpened((prev) => !prev)}
      variant={isSidebarOpened ? 'filled' : 'outlined'}
    >
      {t('фильтры')}
      {filterCounts > 0 && (
        <Badge
          className="VideoAnalyticArchive__FilterBadge"
          value={filterCounts}
        />
      )}
    </Control.Button>
  );

  const columns = useMemo(() => ([
    {
      id: 'image cell', accessor: (value) => value, Cell: ImageCell, width: 200,
    },
    {
      id: 'info cell', accessor: (value) => value, Cell: InfoCell, width: 300,
    },
    {
      id: 'location', accessor: (value) => value?.event, Cell: LocationCell, width: 100,
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [t]);

  const context = {
    ...form,
    resetFilters,
    setFilterCounts,
    lpStreamOptions,
  };
  return (
    <PageWithSidebar
      className="FullHeightSidebar"
      isOpenRightSidebar={isSidebarOpened}
      rightSidebar={(
        <FormProvider {...context}>
          <FiltersHeader
            isAdaptive
            onClick={() => setIsSidebarOpened((prev) => !prev)}
          />
          <VideoAnalyticFilterForm />
        </FormProvider>
      )}
    >
      <div className={cn('VideoAnalyticArchive', { VideoAnalyticArchive__Header_hidden: !isHeaderVisible })}>
        <Page
          actions={actionButtons}
          className="VideoAnalyticArchive__Page"
          title={t('архив видеоаналитик')}
        />
        <div className="VideoAnalyticArchive__TableWrapper">
          <Panel className="VideoAnalyticArchive__Table">
            <Table
              {...tableProps}
              columns={columns}
              noHead
              paginationType="offsetBased"
              ref={tableRef}
              rowProps={(row) => ({ 'data-event-id': row.original?.event?.event_id })}
            />
          </Panel>
        </div>
      </div>
    </PageWithSidebar>
  );
}

export const VideoAnalyticListPage = connect((state) => ({
  lpStreamOptions: selectAnalyticData(state, 'streamOptions'),
}))(VideoAnalyticListPageComponent);
